import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { AnnualLeaseService } from '@app/landowner-admin-shared/services/annual-lease.service';
import AnnualLease from '@app/models/annual-lease';
import PagedResults from '@app/models/paged-results';
import SeasonLookupSearchRequest from '@app/models/season-lookup-search-request';
import SeasonLookupViewModel from '@app/models/season-lookup-view-model';
import { ArrayService, ErrorHelperService, ListingService, LogService, NotificationService } from '@app/services';
import { ValueService } from '@app/services/value.service';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import LeaseSeasonGridContextType from '../lease-season-grid/lease-season-grid-context.enum';
import { YesNoPipe } from '../../../common-components/pipes/yes-no.pipe';
import { SpinnerComponent } from '../../../common-components/spinner/spinner.component';
import { TagComponent } from '../../../common-components/tag/tag.component';
import { OAPopoverComponent } from '../../../common-components/oapopover/oapopover.component';
import { SortIndicatorComponent } from '../../../common-components/sort-indicator/sort-indicator.component';
import { FormsModule } from '@angular/forms';
import { NgIf, NgFor, DatePipe } from '@angular/common';

/**
 * Season Lookup Selecting Grid
 * <season-lookup-grid [title]="'Title goes here'" [lease]="lease"> </season-lookup-grid>
 */
@Component({
    selector: "season-lookup-grid",
    templateUrl: "./season-lookup-grid.component.html",
    styleUrls: ["./season-lookup-grid.component.less"],
    imports: [NgIf, FormsModule, NgFor, SortIndicatorComponent, OAPopoverComponent, TagComponent, SpinnerComponent, DatePipe, YesNoPipe]
})
export class SeasonLookupGridComponent implements OnInit {
  private readonly _componentName = 'SeasonLookupGridComponent';
  private _lease: AnnualLease;

  private listingId: number;
  private _tagNames: string[];
  /**
   * Private field for verbose logging
   */
  private _verboseLogging = false;
  /**
   * Subject to control observable completion
   */
  private unSubSub$ = new Subject<void>();

  private routerEvent$: Subscription;

  private showDevComments$: Subscription;

  public rows: SeasonLookupViewModel[];

  public filteredRows: SeasonLookupViewModel[] = [];

  private pagedResults: PagedResults;

  public retrieving = false;

  @Input()
  public tagsEnabled = false;

  // Filter fields
  public state: string;

  public keyword: string;

  public onlyShowGlobal = false;

  public sortCol = 'name';
  public sortDir = 'asc';

  @Input()
  public contextType: LeaseSeasonGridContextType;

  @Input()
  public title: string;

  public showDevComments: boolean;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private valueService: ValueService,
    private annualLeaseService: AnnualLeaseService,
    private log: LogService,
    private notify: NotificationService,
    private arrayService: ArrayService,
    private errorHelper: ErrorHelperService,
    private listingService: ListingService
  ) {
    this.subscribeRouterEvents();

    this.showDevComments$ = log.showDevComments.subscribe((x) => (this.showDevComments = x));
  }

  ngOnInit() {
    this.retrieveData();
  }

  ngOnDestroy(): void {
    this.unSubSub$.next(void 0);
    this.unSubSub$.complete();

    if (this.routerEvent$) {
      this.routerEvent$.unsubscribe();
    }

    if (this.showDevComments$) {
      this.showDevComments$.unsubscribe();
    }
  }

  /**
   * Is verbose logging enabled
   */
  private get verboseLogging(): boolean {
    return this.log.verboseLogging || this._verboseLogging;
  }

  public get lease(): AnnualLease {
    return this._lease;
  }

  @Input()
  public set lease(v: AnnualLease) {
    this._lease = v;

    if (v) {
      this.listingId = v.listingId;

      this.retrieveListing();
    }
  }

  public get tagNames(): string[] {
    return this._tagNames;
  }
  public set tagNames(v: string[]) {
    this._tagNames = v;
    console.log(`${this._componentName}.tagNames - set v=`, v);
  }

  public get sortDescending(): boolean {
    if (this.sortDir && (this.sortDir.startsWith('d') || this.sortDir.startsWith('D'))) {
      return true;
    }

    return false;
  }

  public setSortCol(col: string) {
    if (col === this.sortCol) {
      // reverse the sort
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortCol = col;
      this.sortDir = 'asc';
    }

    // console.log(`${this._componentName}.setSortCol - sortCol=${this.sortCol},sortDir=${this.sortDir},sortDescending=${this.sortDescending}`);

    this.applyFilters();
  }

  /**
   * Subscribes to Router Events and sets the _verboseLogging flag
   */
  private subscribeRouterEvents() {
    this.routerEvent$ = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        console.log(`${this._componentName}.CTOR router.events NavigationEnd:`, event);

        console.log(
          `${this._componentName}.CTOR router.events NavigationEnd route.snapshot.queryParamMap=`,
          this.route.snapshot.queryParamMap
        );

        if (this.route.snapshot.queryParamMap.has('verboseLogging')) {
          if (this.valueService.getBoolean(this.route.snapshot.queryParamMap.get('verboseLogging')) == true) {
            this._verboseLogging = true;
          } else {
            this._verboseLogging = false;
          }
        }
      }
    });
  }

  private retrieveData() {
    this.retrieving = true;

    let request = { pageNumber: 1 } as SeasonLookupSearchRequest;

    if (this.listingId) {
      request.listingId = this.listingId;
    }

    this.annualLeaseService
      .searchSeasonLookup(request)
      .pipe(takeUntil(this.unSubSub$))
      .subscribe(
        (result) => {
          console.log(`${this._componentName} subscription result=`, result);

          this.pagedResults = result;
          this.rows = result.results.map((x) => x as SeasonLookupViewModel);
          this.retrieving = false;


          if (this.tagsEnabled){
            this.tagNames = Array.from(
              new Set(
                this.rows
                  .filter((x) => x.tags && x.tags.length > 0)
                  .map((x) => x.tags.map((t) => t.name))
                  .reduce((a, v) => a.concat(v))
              )
            );
          }



          this.applyFilters();
        },
        (err) => {
          console.error(err);

          const msg = this.errorHelper.getErrorMessage(err) || 'A subscription error occured';

          console.error(msg);

          this.retrieving = false;

          
          this.notify.error(msg, null);
        }
      );
  }

  /**
   * Exposes the selected filtered rows. This property is what external components
   * will use to access the selected items in the grid
   */
  public get selectedSeasons(): SeasonLookupViewModel[] {
    return this.filteredRows.filter((x) => x.selected);
  }

  private applyFilters() {
    if (this.verboseLogging) {
      console.log(`${this._componentName}.applyFilters()`);
    }

    if (!this.rows) return;

    this.retrieving = true;

    let fr = this.rows.filter((r) => {
      let b = true;

      if (this.state && r.state !== this.state && !r.isGlobal) {
        b = false;
      }

      if (this.onlyShowGlobal && !r.isGlobal) {
        b = false;
      }

      if (this.keyword) {
        if (
          !(r.name.toLowerCase().includes(this.keyword.toLowerCase()) || (r.state + '').toLowerCase().includes(this.keyword.toLowerCase()))
        ) {
          b = false;
        }
      }

      if (b) {
        return r;
      }
    });

    if (this.sortDir === 'desc') {
      fr = this.arrayService.orderByDescending(fr, this.sortCol);
    } else {
      fr = this.arrayService.orderBy(fr, this.sortCol);
    }

    this.filteredRows = fr;

    this.retrieving = false;
  }

  public onSelectAllClick() {
    this.filteredRows.forEach((r) => {
      r.selected = true;
    });
  }
  public onSelectNoneClick() {
    this.filteredRows.forEach((r) => {
      r.selected = false;
    });
  }

  public onKeywordChange(value: string) {
    this.applyFilters();
  }

  public onShowGlobalChange(b: boolean) {
    this.applyFilters();
  }

  public retrieveListing() {
    console.log(`${this._componentName}.retrieveListing()`);

    this.listingService
      .getAsViewModel(this.listingId)
      .pipe(takeUntil(this.unSubSub$))
      .subscribe(
        (result) => {
          console.log(`${this._componentName} subscription result=`, result);

          this.state = result.state;

          this.applyFilters();
        },
        (err) => {
          console.error(err);

          const msg = this.errorHelper.getErrorMessage(err) || 'A subscription error occured';

          console.error(msg);

          
          this.notify.error(msg, null);
        }
      );
  }

  public get showTable(): boolean {
    let b = true;

    if (!this.filteredRows || this.filteredRows.length === 0) {
      b = false;
    }

    if (this.contextType && this.contextType == LeaseSeasonGridContextType.Lease && (!this.lease || !this.state)) {
      b = false;
    }

    return b;
  }

  public onTagButtonClick(tagName) {
    console.log(`${this._componentName}.onTagButtonClick tagName=`, tagName);

    this.filteredRows
      .filter((x) => x.tags && x.tags.length > 0 && x.tags.some((t) => t.name === tagName))
      .forEach((item) => (item.selected = true));
  }
}
